import { useState } from "react";
import { Download, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { promptInstall } from "@/lib/pwa.ts";
import { useInstallKind } from "@/lib/useInstall.ts";
import { cn } from "@/lib/utils";

/**
 * "Put it on your home screen." Shows the right thing for this device and nothing at all when there is nothing to
 * offer (already installed, or a browser that can't). With `onDismiss` it gets a "Not now" and is meant for a moment
 * of goodwill (right after a solve); without it, it is a permanent line in the help overlay.
 */
export function InstallCard({ onDismiss, className }: { onDismiss?: () => void; className?: string }) {
  const kind = useInstallKind();
  const [declined, setDeclined] = useState(false);
  // The browser's prompt can only be shown once, so after a "no" the card has nothing to offer but a kind word.
  if (kind === "installed" || (kind === "none" && !declined)) return null;

  const install = async () => {
    if (!(await promptInstall())) setDeclined(true);
  };

  return (
    <section aria-labelledby="install-h" className={cn("rounded-xl border border-border bg-card px-5 py-4 text-left font-ui shadow-sm", className)}>
      <h3 id="install-h" className="font-display text-xl leading-tight">
        Keep it on your home screen
      </h3>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
        {declined && "No problem. Whenever you like, it’s in your browser’s menu as Install or Add to Home Screen."}
        {!declined && kind === "prompt" && "Open today’s verse in one tap, full screen, like any other app."}
        {!declined && kind === "ios" && (
          <>
            Open today’s verse in one tap, full screen. In Safari, tap the Share button (
            <Share className="mx-0.5 inline size-3.5 -translate-y-px" role="img" aria-label="Share" />
            ), then choose <strong className="font-semibold text-foreground">Add to Home Screen</strong>.
          </>
        )}
        {!declined && kind === "android" && "Open today’s verse in one tap, full screen. In Chrome, tap the menu at the top right, then choose Install app (or Add to Home screen)."}
        {!declined && kind === "ios-inapp" && "This page is open inside another app. Open it in Safari first, then tap Share and choose Add to Home Screen."}
      </p>
      {((kind === "prompt" && !declined) || onDismiss) && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {kind === "prompt" && !declined && (
            <Button size="sm" onClick={install} className="gap-1.5">
              <Download aria-hidden /> Install
            </Button>
          )}
          {onDismiss && (
            <Button size="sm" variant="ghost" onClick={onDismiss} className="text-muted-foreground">
              Not now
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
